import React, { useEffect, useRef, useCallback } from "react";
import { readConsent, applyConsent } from './consentManager';
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import "./App.scss";
import LandingPage from "./components/landingPage/";
import ContactUs from "./components/contactUs/";
import Store from "./components/store";
import ProductAssets from "./components/productAssets";
import ComingSoon from "./components/comingSoon";
import Pricing from "./components/pricing";
import PrivacyAndTerms from "./components/privacyAndTerms";
import AboutUsNew from "./components/aboutUs/AboutUsNew.jsx";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { Switch } from "react-router";
import SkylineBohoRenders from "./components/skylineBohoRenders";
import ProductPage from "./components/productPage";
import SpacePage from "./components/spacePage";
import SkylineDecoRenders from "./components/skylineDecoRenders";
import preppyRenders from "./components/preppyRenders";
import FarmhouseRenders from "./components/farmhouseRenders";
import SubscriptionPlans from "./components/SubscriptionPlans/SubscriptionPlans.jsx";
import PricingPage from "./components/pricingV2/index.jsx";
import UnitPrice from "./components/UnitPrice/UnitPrice.jsx";
import FAQs from "./components/FAQs";
import HowALL3DWorks from "./components/HowALL3DWorks/HowALL3DWorks";
import SceneDetails from "./components/SceneDetail";
import HighPointLandingPage from "./components/HighPointLandingPage";
import LifestylesFeed from "./components/LifestylesFeed/LifestylesFeed";
import Integrations from "./components/Integrations/Integrations"
import Amazon from "./components/Amazon/Amazon.jsx";
import ThreeDExpertTerms from "./components/privacyAndTerms/ThreeDExpertTerms.jsx"
import Termsandconditions from "./components/Termsandconditions/Termsandconditions.jsx"
import QuickLook from "./components/quickLook/QuickLook.jsx";
import AICreator from "./components/AICreator/AICreator.jsx";
import AiFeed from "./components/AIFeed/AiFeed.jsx";
import Walmart from "./components/Amazon/Walmart.jsx";
import SMBLandingPage from "./components/SMBLandingPage/SMBLandingPage.jsx";
import Homepage from "./components/Homepage/Homepage.jsx";
import AILandingPageNew from "./components/AILandingPageNew";
import ImageGalleryPage from "./components/AILandingPageNew/ImageGalleryPage";
import LandingV2 from "./components/LandingV2/LandingV2.jsx";
import CookieConsentBanner from "./components/cookieConsentBanner";

const PENDING_HOMEPAGE_VISIT_KEY = "all3d_pending_homepage_visit";

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {}
}

function safeSessionRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {}
}

function safeLocalGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function HomepageVisitTracker() {
  const location = useLocation();
  const trackedSignatureRef = useRef("");

  const trackHomepageVisit = useCallback(
    (signature, payload) => {
      const consent = readConsent();
      if (consent && !consent.analytics) {
        safeSessionRemove(PENDING_HOMEPAGE_VISIT_KEY);
        return;
      }

      if (!consent) {
        safeSessionSet(
          PENDING_HOMEPAGE_VISIT_KEY,
          JSON.stringify({ signature, payload })
        );
        return;
      }

      if (!window._posthogLoaded) {
        safeSessionSet(
          PENDING_HOMEPAGE_VISIT_KEY,
          JSON.stringify({ signature, payload })
        );
        return;
      }

      try {
        posthog.register({ utm_source: payload.utmSource });
        posthog.capture("homepage_visit", {
          location: "/",
          utm_source: payload.utmSource,
          email: payload.email,
        });
      } catch {
        safeSessionSet(
          PENDING_HOMEPAGE_VISIT_KEY,
          JSON.stringify({ signature, payload })
        );
        return;
      }

      trackedSignatureRef.current = signature;
      safeSessionRemove(PENDING_HOMEPAGE_VISIT_KEY);
    },
    []
  );

  useEffect(() => {
    if (location.pathname !== "/") return;

    const urlParams = new URLSearchParams(location.search);
    const sourceFromUrl = urlParams.get("utm_source");
    if (sourceFromUrl) {
      safeLocalSet("utm_source", sourceFromUrl);
    }

    const signature = `${location.pathname}${location.search}`;
    if (trackedSignatureRef.current === signature) return;

    const payload = {
      utmSource: sourceFromUrl || safeLocalGet("utm_source") || "direct",
      email: urlParams.get("email") || "",
    };

    trackHomepageVisit(signature, payload);
  }, [location.pathname, location.search, trackHomepageVisit]);

  useEffect(() => {
    const handleConsentUpdated = () => {
      if (!window._posthogLoaded) return;

      const pendingRaw = safeSessionGet(PENDING_HOMEPAGE_VISIT_KEY);
      if (!pendingRaw) return;

      try {
        const pending = JSON.parse(pendingRaw);
        if (!pending?.signature || !pending?.payload) {
          safeSessionRemove(PENDING_HOMEPAGE_VISIT_KEY);
          return;
        }
        if (trackedSignatureRef.current === pending.signature) {
          safeSessionRemove(PENDING_HOMEPAGE_VISIT_KEY);
          return;
        }
        trackHomepageVisit(pending.signature, pending.payload);
      } catch {
        safeSessionRemove(PENDING_HOMEPAGE_VISIT_KEY);
      }
    };

    window.addEventListener("all3d:consent-updated", handleConsentUpdated);
    return () =>
      window.removeEventListener("all3d:consent-updated", handleConsentUpdated);
  }, [trackHomepageVisit]);

  return null;
}

function App() {
  const reload = () => window.location.reload();

  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      try {
        applyConsent(existing);
      } catch {}
    }
  }, []);

  return (
    <>
      <Router>
        <ScrollToTop />
        <HomepageVisitTracker />
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <main id="main-content">
        <Switch>
          <Route exact path="/" component={LandingV2} />
          <Route exact path="/enterprise-features" component={Homepage} />
          <Route exact path="/new" component={Homepage} />

          <Route exact path="/smb" component={SMBLandingPage} />
          <Route exact path="/landingv2" component={LandingV2} />

          <Route exact path="/contact-us" component={ContactUs} />
          
          <Route exact path="/ai-creator" component={ImageGalleryPage} />
          <Route exact path="/ai-creator-page" component={AICreator} />
          <Route exact path="/ai-images" component={ImageGalleryPage} />

          <Route exact path="/store" component={Store} />
          <Route exact path="/store/:brand" component={Store} />
          <Route exact path="/product-assets/:id" component={ProductPage} />
          <Route exact path="/room-assets/:id" component={SpacePage} />
          <Route exact path="/scene-assets/:id" component={SceneDetails} />
          <Route exact path="/pricingV2" component={PricingPage} />
          <Route exact path="/pricing" component={PricingPage} />

          {/* <Route exact path="/pricing" component={SubscriptionPlans} /> */}
          <Route exact path="/faqs" component={FAQs} />
          <Route exact path="/unit-price" component={UnitPrice} />
          <Route exact path="/integrations" component={Integrations} />
          <Route exact path="/amazon" component={Amazon} />
          <Route exact path="/walmart" component={Walmart} />
          <Route exact path="/termsandconditions" component={Termsandconditions} />
          <Route exact path="/quicklook" component={QuickLook} />
          <Route
            exact
            path={"/terms"}
            render={() => <PrivacyAndTerms tab="terms" />}
          />
          <Route
            exact
            path={"/privacy"}
            render={() => <PrivacyAndTerms tab="privacy" />}
          />
          <Route exact path="/about-us" component={AboutUsNew} />
          <Route exact path="/blog" render={() => { window.location.replace("https://blog.all3d.ai"); return null; }} />
          <Route exact path="/blog/:id" render={({ match }) => { window.location.replace(`https://blog.all3d.ai/${match.params.id}/`); return null; }} />
          <Route exact path="/how-all3d-works" component={HowALL3DWorks} />
          <Route
            exact
            path="/skyline/modernboho/images"
            component={SkylineBohoRenders}
          />
          <Route
            exact
            path="/skyline/decoglam/images"
            component={SkylineDecoRenders}
          />
          <Route
            exact
            path="/skyline/preppytradional/images"
            component={preppyRenders}
          />
          <Route
            exact
            path="/skyline/farmhouse/images"
            component={FarmhouseRenders}
          />
          <Route path="/sitemap.xml" onEnter={reload} />
          <Route
            exact
            path="/kayak-ar"
            component={() => {
              window.location.href =
                "https://d48f7equ64qjl.cloudfront.net/product_assets/usdz/5318.usdz";
              return null;
            }}
          />
          <Route
            exact
            path="/kayak-3d"
            component={() => {
              window.location.href =
                "https://app.all3d.ai/product_public_viewer/product_3d_model/5318";
              return null;
            }}
          />
          <Route
            exact
            path="/kayak-prerendered-360"
            component={() => {
              window.location.href =
                "https://app.all3d.ai/product_public_viewer/product_360/5251";
              return null;
            }}
          />

          <Route
            exact
            path="/high-point-landing-page"
            component={HighPointLandingPage}
          />

          <Route
            exact
            path="/feed"
            component={LifestylesFeed}
          />
          <Route
            exact
            path="/ai-feed"
            component={AiFeed}
          />
          <Route
            exact
            path="/three-d-terms"
            component={ThreeDExpertTerms}
          />

          <Route path="/" component={LandingPage} />
        </Switch>
        </main>
        <CookieConsentBanner />
      </Router>
    </>
  );
}

export default App;
